import { useState, useCallback } from "react";
import { HERO_IMAGE_DEFAULT } from "@/config/images";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type Props = {
  imageSrc?: string;
  imageAlt: string;
  badge?: string;
  title: string;
  subtitle: string;
  primaryCta: { href: string; label: string };
  secondaryCta?: { href: string; label: string };
  trustSignals?: string[];
};

export function HeroFull({
  imageSrc,
  imageAlt,
  badge,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  trustSignals,
}: Props) {
  const fallbackSrc = imageSrc ?? HERO_IMAGE_DEFAULT;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(fallbackSrc);

  const handleError = useCallback(() => {
    // Retry without query params if the original URL fails
    if (currentSrc.includes("?")) {
      const cleanSrc = currentSrc.split("?")[0];
      setCurrentSrc(cleanSrc);
    }
  }, [currentSrc]);

  return (
    <section className="relative w-full min-h-[520px] md:min-h-[680px] overflow-hidden">
      <div className="absolute inset-0 z-0 bg-blue-800">
        <img
          src={currentSrc}
          alt={imageAlt}
          className={`w-full h-full object-cover object-center transition-opacity duration-700 ${
            imgLoaded ? "opacity-100" : "opacity-0"
          }`}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1080}
          onLoad={() => setImgLoaded(true)}
          onError={handleError}
        />
      </div>
      <div className="absolute inset-0 z-10 bg-gradient-to-br from-black/65 via-black/45 to-black/55"></div>
      <div className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-28 flex flex-col justify-center min-h-[520px] md:min-h-[680px]">
        {badge && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-yellow-400 text-gray-900 rounded-full text-xs md:text-sm font-semibold w-fit mb-4 md:mb-6">
            {badge}
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white leading-tight max-w-3xl mb-4 md:mb-5 drop-shadow-lg">
          {title}
        </h1>
        <p className="text-base md:text-xl text-white max-w-2xl mb-6 md:mb-8 leading-relaxed line-clamp-3 md:line-clamp-none drop-shadow-md">
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-8">
          <a
            href={primaryCta.href}
            className="inline-flex items-center justify-center gap-2 px-5 py-4 md:px-6 md:py-4 bg-yellow-400 hover:bg-orange-500 text-gray-900 font-bold rounded-full shadow-lg transition-all text-base md:text-lg w-full sm:w-auto"
          >
            📞 {primaryCta.label}
          </a>
          {secondaryCta && (
            <a
              href={secondaryCta.href}
              className="inline-flex items-center justify-center gap-2 px-5 py-4 md:px-6 md:py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white text-white font-bold rounded-full transition-all text-base md:text-lg w-full sm:w-auto"
            >
              {secondaryCta.label} →
            </a>
          )}
        </div>
        {trustSignals && (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs sm:text-sm text-white/90">
            {trustSignals.slice(0, 3).map((s) => (
              <span key={s} className="whitespace-nowrap">
                ✓ {s}
              </span>
            ))}
            {trustSignals.slice(3).map((s) => (
              <span key={s} className="hidden md:inline whitespace-nowrap">
                ✓ {s}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}